import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { FiRefreshCw } from 'react-icons/fi';
import ScreenContainer from '../components/ScreenContainer';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchEmailById, reprocessEmail } from '../store/slices/emailsSlice';
import { formatDate } from '../utils/formatters';

interface TimelineEvent {
  stage: string;
  status: string;
  title: string;
  message?: string | null;
  details?: Record<string, unknown> | null;
}

interface ProcessingTimeline {
  finalStatus?: string;
  transactionsCreated?: number;
  timeline?: TimelineEvent[];
}

const statusBadgeClass: Record<string, string> = {
  RECEIVED: 'bg-muted text-muted-foreground',
  REPROCESSING: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  PARSE_FAILED: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  PROCESSED: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  PROCESS_FAILED: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
};

const timelineStatusClass: Record<string, string> = {
  SUCCESS: 'border-green-500 bg-green-500 text-white',
  MISMATCH: 'border-amber-500 bg-amber-500 text-white',
  SKIPPED: 'border-muted-foreground bg-muted text-muted-foreground',
  REJECTED: 'border-red-500 bg-red-500 text-white',
  DUPLICATE: 'border-amber-500 bg-amber-500 text-white',
  RETRYABLE_FAILURE: 'border-orange-500 bg-orange-500 text-white',
  FAILED: 'border-red-500 bg-red-500 text-white',
};

const PROCESSING_STATUSES = new Set(['REPROCESSING']);
const POLL_INTERVAL_MS = 3000;

const formatDetailValue = (value: unknown): string => {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const isDetailObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const formatDetailLabel = (key: string): string =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

function DetailValue({ value }: { value: unknown }) {
  if (isStringArray(value)) {
    return (
      <ul className="space-y-1 text-foreground">
        {value.map((item) => (
          <li key={item} className="break-words [overflow-wrap:anywhere]">
            {item}
          </li>
        ))}
      </ul>
    );
  }

  if (isDetailObject(value)) {
    return (
      <dl className="w-full space-y-3 text-foreground">
        {Object.entries(value).map(([objectKey, objectValue]) => (
          <div key={objectKey} className="min-w-0 space-y-1">
            <dt className="text-muted-foreground">{formatDetailLabel(objectKey)}</dt>
            <dd className="min-w-0 break-words pl-0 text-foreground [overflow-wrap:anywhere]">
              <DetailValue value={objectValue} />
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  return (
    <span className="break-words text-foreground [overflow-wrap:anywhere]">
      {formatDetailValue(value)}
    </span>
  );
}

function EmailDetailsScreen() {
  const { emailId } = useParams();
  const dispatch = useAppDispatch();

  const parsedId = useMemo(() => {
    if (!emailId) {
      return null;
    }
    const value = Number(emailId);
    return Number.isFinite(value) ? value : null;
  }, [emailId]);

  const email = useAppSelector((state) =>
    parsedId == null ? null : state.emails.detailsById[parsedId]
  );
  const detailStatus = useAppSelector((state) =>
    parsedId == null ? 'failed' : state.emails.detailStatusById[parsedId] ?? 'idle'
  );
  const detailError = useAppSelector((state) =>
    parsedId == null ? 'Invalid email ID' : state.emails.detailErrorById[parsedId] ?? null
  );
  const reprocessStatus = useAppSelector((state) =>
    parsedId == null ? 'idle' : state.emails.reprocessStatusById[parsedId] ?? 'idle'
  );
  const reprocessError = useAppSelector((state) =>
    parsedId == null ? null : state.emails.reprocessErrorById[parsedId] ?? null
  );

  useEffect(() => {
    if (parsedId == null) {
      return;
    }
    if (!email && detailStatus === 'idle') {
      dispatch(fetchEmailById(parsedId));
    }
  }, [dispatch, parsedId, email, detailStatus]);

  useEffect(() => {
    if (parsedId == null || !email || !PROCESSING_STATUSES.has(email.processingStatus)) {
      return;
    }

    const intervalId = window.setInterval(() => {
      dispatch(fetchEmailById({ emailId: parsedId, forceRefresh: true }));
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [dispatch, parsedId, email]);

  const processingTimeline = useMemo(() => {
    if (!email?.processingTimelineJson) {
      return { timeline: null, raw: null, parseError: false };
    }
    try {
      const parsed = JSON.parse(email.processingTimelineJson) as ProcessingTimeline;
      return {
        timeline: Array.isArray(parsed.timeline) ? parsed.timeline : null,
        raw: Array.isArray(parsed.timeline) ? null : email.processingTimelineJson,
        parseError: !Array.isArray(parsed.timeline),
      };
    } catch {
      return { timeline: null, raw: email.processingTimelineJson, parseError: true };
    }
  }, [email?.processingTimelineJson]);

  const isProcessing = email ? PROCESSING_STATUSES.has(email.processingStatus) : false;
  const isSubmittingReprocess = reprocessStatus === 'loading';

  return (
    <ScreenContainer title="Email Details">
      <div
        className="mx-auto w-full max-w-xl p-4 space-y-4"
        style={{ userSelect: 'text', WebkitUserSelect: 'text', cursor: 'auto' }}
      >
        {parsedId == null && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300 dark:border-red-900/40">
            Invalid email ID.
          </div>
        )}

        {parsedId != null && detailStatus === 'loading' && !email && (
          <div className="space-y-3">
            <div className="h-12 rounded-lg bg-muted animate-pulse" />
            <div className="h-24 rounded-lg bg-muted animate-pulse" />
            <div className="h-48 rounded-lg bg-muted animate-pulse" />
          </div>
        )}

        {parsedId != null && detailStatus === 'failed' && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300 dark:border-red-900/40">
            {detailError || 'Failed to load email details.'}
          </div>
        )}

        {email && (
          <>
            <div className="">
              <h2 className="text-lg font-semibold text-foreground break-all whitespace-pre-wrap [overflow-wrap:anywhere]">
                {email.subject || '(No Subject)'}
              </h2>
            </div>
            <div className="space-y-1 text-sm text-muted-foreground">
              <p className="break-all whitespace-pre-wrap [overflow-wrap:anywhere]">
                <span className="font-medium text-foreground">From:</span>{' '}
                <span>{email.headerFrom || email.envelopeSender || 'Unknown'}</span>
              </p>
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="space-y-1">
                  <p className="break-all whitespace-pre-wrap [overflow-wrap:anywhere]">
                    <span className="font-medium text-foreground">Message Date:</span>{' '}
                    <span>{formatDate(email.messageDateTime || email.receivedAt, true)}</span>
                  </p>
                  <p className="break-all whitespace-pre-wrap [overflow-wrap:anywhere]">
                    <span className="font-medium text-foreground">Received:</span>{' '}
                    <span>{formatDate(email.receivedAt, true)}</span>
                  </p>
                </div>
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusBadgeClass[email.processingStatus] || statusBadgeClass.RECEIVED}`}>
                  {email.processingStatus}
                </span>
              </div>
              {email.processingError && (
                <p className="text-red-600 dark:text-red-300 break-all whitespace-pre-wrap [overflow-wrap:anywhere]">
                  <span className="font-medium">Processing Error:</span>{' '}
                  <span>{email.processingError}</span>
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={() => dispatch(reprocessEmail(email.id))}
              disabled={isProcessing || isSubmittingReprocess}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FiRefreshCw className={isProcessing || isSubmittingReprocess ? 'animate-spin' : ''} />
              {isProcessing || isSubmittingReprocess ? 'Processing email...' : 'Reprocess email'}
            </button>

            {reprocessError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
                {reprocessError}
              </div>
            )}

            <div className="border-t border-border" />

            <div className="w-full overflow-x-auto">
              <div className="m-0 text-left whitespace-pre-wrap break-words text-sm text-foreground">
                {email.parsedTextBody || 'No parsed body available.'}
              </div>
            </div>

            {(processingTimeline.timeline || processingTimeline.raw) && (
              <>
                <div className="border-t border-border" />
                <div className="w-full">
                  <h3 className="mb-2 text-sm font-semibold text-foreground">Processing Audit Trail</h3>
                  {processingTimeline.timeline && (
                    <ol className="space-y-4">
                      {processingTimeline.timeline.map((event, index) => {
                        const details = event.details ? Object.entries(event.details) : [];
                        return (
                          <li key={`${event.stage}-${index}`} className="relative pl-8">
                            {index < processingTimeline.timeline!.length - 1 && (
                              <span className="absolute left-[9px] top-5 h-[calc(100%+0.5rem)] w-px bg-border" />
                            )}
                            <span
                              className={`absolute left-0 top-1 flex h-5 w-5 items-center justify-center rounded-full border text-[10px] font-semibold ${
                                timelineStatusClass[event.status] || 'border-border bg-muted text-muted-foreground'
                              }`}
                            >
                              {index + 1}
                            </span>
                            <div className="space-y-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="text-sm font-medium text-foreground">{event.title || event.stage}</p>
                                <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                                  {event.status}
                                </span>
                              </div>
                              {event.message && (
                                <p className="text-xs text-muted-foreground break-words [overflow-wrap:anywhere]">
                                  {event.message}
                                </p>
                              )}
                              {details.length > 0 && (
                                <dl className="grid grid-cols-1 gap-2 rounded-md bg-muted/60 p-2 text-xs">
                                  {details.map(([key, value]) => (
                                    <div
                                      key={key}
                                      className="min-w-0"
                                    >
                                      <dt className="font-medium text-muted-foreground">{key}</dt>
                                      {key === 'accounts' && isStringArray(value) ? (
                                        <dd className="-mx-1 mt-1 overflow-x-auto px-1 pb-1">
                                          <div className="flex w-max max-w-none gap-2">
                                            {value.map((account) => (
                                              <span
                                                key={account}
                                                className="whitespace-nowrap rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-foreground"
                                              >
                                                {account}
                                              </span>
                                            ))}
                                          </div>
                                        </dd>
                                      ) : (
                                        <dd className="min-w-0">
                                          <DetailValue value={value} />
                                        </dd>
                                      )}
                                    </div>
                                  ))}
                                </dl>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                  {processingTimeline.parseError && processingTimeline.raw && (
                    <div className="space-y-2">
                      <p className="text-xs text-red-600 dark:text-red-300">
                        Timeline JSON could not be parsed.
                      </p>
                      <pre className="m-0 whitespace-pre-wrap break-all rounded-md bg-muted p-3 text-xs text-foreground [overflow-wrap:anywhere]">
                        {processingTimeline.raw}
                      </pre>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </ScreenContainer>
  );
}

export default EmailDetailsScreen;
